import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface AuthGuardProps {
  children?: React.ReactNode;
  requireAuth?: boolean;
  requireArtist?: boolean;
  requireApprovedArtist?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = false,
  requireArtist = false,
  requireApprovedArtist = false,
}) => {
  const { user, profile, artistProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-royal-gold/30 border-t-royal-gold rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-royal-gold rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if ((requireAuth || requireArtist || requireApprovedArtist) && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireArtist) {
    const isArtist = profile?.is_artist === true;
    const hasArtistProfile = artistProfile !== null;

    if (!isArtist || !hasArtistProfile) {
      return <Navigate to="/become-artist" state={{ from: location }} replace />;
    }
  }

  if (requireApprovedArtist) {
    if (!artistProfile) {
      return <Navigate to="/become-artist" state={{ from: location }} replace />;
    }

    if (artistProfile.approval_status === 'pending') {
      return <Navigate to="/artist/pending" state={{ from: location }} replace />;
    }

    if (artistProfile.approval_status === 'rejected') {
      return <Navigate to="/become-artist" state={{ from: location, rejected: true }} replace />;
    }

    if (artistProfile.approval_status !== 'approved') {
      return <Navigate to="/become-artist" state={{ from: location }} replace />;
    }
  }

  return <>{children || <Outlet />}</>;
};